import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, MessageSquare, FileText, Users, Bell, Clock,
  Download, ChevronRight, User, Calendar, AlertCircle, Send,
  Pin, Lock, Plus, Folder, MessageSquareOff
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import Layout from '../layout/Layout';

const EspaceElevePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [forumPosts, setForumPosts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPostForm, setNewPostForm] = useState({ title: '', content: '' });
  const [replyContent, setReplyContent] = useState('');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [forumEnabled, setForumEnabled] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'eleve' || !user.is_approved) {
      navigate('/login');
      return;
    }
    loadDashboard();
    loadForumStatus();
  }, [user, navigate]);

  const loadForumStatus = async () => {
    try {
      const res = await api.get('/settings/site-info');
      setForumEnabled(res.data?.forum_enabled !== false);
    } catch (error) {
      // fallback: keep default true
    }
  };

  const loadDashboard = async () => {
    try {
      const res = await api.get('/eleve/dashboard');
      setDashboardData(res.data);
      if (res.data.groupe) {
        loadForumPosts(res.data.groupe.id);
        loadDocuments(res.data.groupe.id);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger le tableau de bord', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadForumPosts = async (groupeId) => {
    try {
      const res = await api.get(`/groupes/${groupeId}/forum`);
      setForumPosts(res.data);
    } catch (error) {
      console.error('Error loading forum posts:', error);
    }
  };

  const loadDocuments = async (groupeId) => {
    try {
      const res = await api.get(`/groupes/${groupeId}/documents`);
      setDocuments(res.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadPostDetails = async (postId) => {
    try {
      const res = await api.get(`/forum/posts/${postId}`);
      setSelectedPost(res.data);
    } catch (error) {
      console.error('Error loading post:', error);
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!dashboardData?.groupe) return;
    try {
      await api.post(`/groupes/${dashboardData.groupe.id}/forum`, null, {
        params: { title: newPostForm.title, content: newPostForm.content }
      });
      toast({ title: 'Discussion créée' });
      setShowNewPostModal(false);
      setNewPostForm({ title: '', content: '' });
      loadForumPosts(dashboardData.groupe.id);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de créer la discussion', variant: 'destructive' });
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!selectedPost || !replyContent.trim()) return;
    try {
      await api.post(`/forum/posts/${selectedPost.id}/reply`, null, {
        params: { content: replyContent }
      });
      setReplyContent('');
      loadPostDetails(selectedPost.id);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer la réponse', variant: 'destructive' });
    }
  };

  const getFileTypeColor = (type) => {
    switch (type) {
      case 'exercise': return 'bg-blue-100 text-blue-700';
      case 'homework': return 'bg-orange-100 text-orange-700';
      case 'correction': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getFileTypeLabel = (type) => {
    switch (type) {
      case 'exercise': return 'Exercice';
      case 'homework': return 'Devoir';
      case 'correction': return 'Correction';
      default: return 'Document';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#285BBB]"></div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData?.groupe) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#F5F7FF] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#2C3C65] mb-4">En attente d'affectation</h1>
            <p className="text-[#6777A1]">
              Votre compte a été approuvé mais vous n'êtes pas encore affecté à un groupe.
              Veuillez patienter ou contacter l'école.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F7FF]" data-testid="espace-eleve-page">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#285BBB] to-[#2C3C65] text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Espace Élève</h1>
                <p className="text-blue-100 mt-1">Bienvenue, {user?.prenom} !</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                <p className="text-sm text-blue-200">Groupe</p>
                <p className="font-semibold">{dashboardData.groupe.nom}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-1 overflow-x-auto">
              {[
                { id: 'dashboard', label: 'Tableau de bord', icon: BookOpen },
                ...(forumEnabled ? [{ id: 'forum', label: 'Forum', icon: MessageSquare }] : []),
                { id: 'documents', label: 'Documents', icon: FileText },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setSelectedPost(null); }}
                    className={`flex items-center px-4 py-4 font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-[#285BBB] text-[#285BBB]'
                        : 'border-transparent text-[#6777A1] hover:text-[#2C3C65]'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                {/* Groupe Info */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-lg font-semibold text-[#2C3C65] mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-[#285BBB]" />
                    Mon groupe
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-[#F5F7FF] rounded-lg p-4">
                      <p className="text-sm text-[#6777A1]">Nom du groupe</p>
                      <p className="font-semibold text-[#2C3C65]">{dashboardData.groupe.nom}</p>
                    </div>
                    <div className="bg-[#F5F7FF] rounded-lg p-4">
                      <p className="text-sm text-[#6777A1]">Niveau</p>
                      <p className="font-semibold text-[#2C3C65]">{dashboardData.groupe.niveau || 'Non défini'}</p>
                    </div>
                    <div className="bg-[#F5F7FF] rounded-lg p-4">
                      <p className="text-sm text-[#6777A1]">Horaire</p>
                      <p className="font-semibold text-[#2C3C65]">{dashboardData.groupe.horaire || 'Non défini'}</p>
                    </div>
                    <div className="bg-[#F5F7FF] rounded-lg p-4">
                      <p className="text-sm text-[#6777A1]">Professeur</p>
                      <p className="font-semibold text-[#2C3C65]">{dashboardData.groupe.professeur || 'Non défini'}</p>
                    </div>
                  </div>
                </div>

                {/* Recent Forum Posts */}
                {forumEnabled && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-[#2C3C65] flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2 text-[#285BBB]" />
                      Dernières discussions
                    </h2>
                    <Button variant="ghost" onClick={() => setActiveTab('forum')} className="text-[#285BBB]">
                      Voir tout <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                  {dashboardData.recent_posts?.length === 0 ? (
                    <p className="text-[#6777A1] text-center py-4">Aucune discussion</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.recent_posts?.slice(0, 3).map((post) => (
                        <div
                          key={post.id}
                          onClick={() => { setActiveTab('forum'); loadPostDetails(post.id); }}
                          className="p-4 bg-[#F5F7FF] rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-[#2C3C65]">{post.title}</p>
                              <p className="text-sm text-[#6777A1] mt-1">Par {post.author_name}</p>
                            </div>
                            <span className="text-xs text-[#6777A1]">
                              {new Date(post.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Recent Documents */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-[#2C3C65] flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-[#285BBB]" />
                      Documents récents
                    </h2>
                  </div>
                  {dashboardData.recent_documents?.length === 0 ? (
                    <p className="text-[#6777A1] text-center py-4">Aucun document</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.recent_documents?.map((doc) => (
                        <a
                          key={doc.id}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.file_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-[#F5F7FF] rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <Folder className="h-5 w-5 text-[#285BBB] mr-3" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#2C3C65] truncate">{doc.title}</p>
                            <p className="text-xs text-[#6777A1]">{doc.filename}</p>
                          </div>
                          <Download className="h-4 w-4 text-[#6777A1]" />
                        </a>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" onClick={() => setActiveTab('documents')} className="w-full mt-4">
                    Voir tous les documents
                  </Button>
                </div>

                {/* Communications */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-lg font-semibold text-[#2C3C65] flex items-center mb-4">
                    <Bell className="h-5 w-5 mr-2 text-[#285BBB]" />
                    Informations
                  </h2>
                  {dashboardData.communications?.length === 0 ? (
                    <p className="text-[#6777A1] text-center py-4">Aucune information</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.communications?.slice(0, 3).map((comm) => (
                        <div
                          key={comm.id}
                          className={`p-3 rounded-lg border-l-4 ${
                            comm.type === 'urgent' ? 'bg-red-50 border-red-500' :
                            comm.type === 'event' ? 'bg-purple-50 border-purple-500' :
                            'bg-blue-50 border-blue-500'
                          }`}
                        >
                          <p className="font-medium text-sm text-[#2C3C65]">{comm.title}</p>
                          <p className="text-xs text-[#6777A1] mt-1 line-clamp-2">{comm.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Forum Tab */}
          {activeTab === 'forum' && forumEnabled && (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Posts List */}
              <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4 border-b bg-[#F5F7FF] flex justify-between items-center">
                  <h2 className="font-semibold text-[#2C3C65]">Discussions</h2>
                  <Button onClick={() => setShowNewPostModal(true)} size="sm" className="bg-[#285BBB]">
                    <Plus className="h-4 w-4 mr-1" /> Nouveau
                  </Button>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {forumPosts.length === 0 ? (
                    <p className="text-[#6777A1] text-center py-8">Aucune discussion</p>
                  ) : (
                    forumPosts.map((post) => (
                      <div
                        key={post.id}
                        onClick={() => loadPostDetails(post.id)}
                        className={`p-4 cursor-pointer hover:bg-[#F5F7FF] transition-colors ${
                          selectedPost?.id === post.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start">
                          {post.is_pinned && <Pin className="h-4 w-4 text-[#ff3a2d] mr-2 flex-shrink-0" />}
                          {post.is_locked && <Lock className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#2C3C65] truncate">{post.title}</p>
                            <div className="flex items-center mt-1 text-xs text-[#6777A1]">
                              <User className="h-3 w-3 mr-1" />
                              {post.author_name}
                              <span className="mx-2">•</span>
                              <span>{post.reply_count || 0} réponses</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Post Detail */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-md">
                {selectedPost ? (
                  <div className="h-full flex flex-col">
                    <div className="p-6 border-b">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-xl font-bold text-[#2C3C65]">{selectedPost.title}</h2>
                          <div className="flex items-center mt-2 text-sm text-[#6777A1]">
                            <User className="h-4 w-4 mr-1" />
                            {selectedPost.author_name}
                            <span className="mx-2">•</span>
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(selectedPost.created_at).toLocaleString('fr-FR')}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {selectedPost.is_pinned && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center">
                              <Pin className="h-3 w-3 mr-1" /> Épinglé
                            </span>
                          )}
                          {selectedPost.is_locked && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full flex items-center">
                              <Lock className="h-3 w-3 mr-1" /> Verrouillé
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 p-4 bg-[#F5F7FF] rounded-lg">
                        <p className="text-[#2C3C65] whitespace-pre-wrap">{selectedPost.content}</p>
                      </div>
                    </div>

                    {/* Replies */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[400px]">
                      {selectedPost.replies?.length === 0 ? (
                        <p className="text-[#6777A1] text-center py-4">Aucune réponse</p>
                      ) : (
                        selectedPost.replies?.map((reply) => (
                          <div key={reply.id} className="flex space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                              reply.author_role === 'admin' || reply.author_role === 'super_admin'
                                ? 'bg-[#ff3a2d]'
                                : 'bg-[#285BBB]'
                            }`}>
                              {reply.author_name?.[0]}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <span className="font-medium text-[#2C3C65]">{reply.author_name}</span>
                                {(reply.author_role === 'admin' || reply.author_role === 'super_admin') && (
                                  <span className="ml-2 px-2 py-0.5 bg-[#ff3a2d] text-white text-xs rounded">Prof</span>
                                )}
                                <span className="ml-2 text-xs text-[#6777A1]">
                                  {new Date(reply.created_at).toLocaleString('fr-FR')}
                                </span>
                              </div>
                              <p className="mt-1 text-[#6777A1]">{reply.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Reply Form */}
                    {!selectedPost.is_locked && (
                      <div className="p-4 border-t bg-[#F5F7FF]">
                        <form onSubmit={submitReply} className="flex space-x-3">
                          <Textarea
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="Écrire une réponse..."
                            rows={2}
                            className="flex-1"
                          />
                          <Button type="submit" className="bg-[#285BBB]">
                            <Send className="h-4 w-4" />
                          </Button>
                        </form>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                      <p className="text-[#6777A1]">Sélectionnez une discussion</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl shadow-md">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-[#2C3C65]">Documents du groupe</h2>
                <p className="text-[#6777A1] mt-1">Exercices, devoirs et ressources partagés par le professeur</p>
              </div>
              {documents.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-[#6777A1]">Aucun document disponible</p>
                </div>
              ) : (
                <div className="divide-y">
                  {documents.map((doc) => (
                    <div key={doc.id} className="p-4 hover:bg-[#F5F7FF] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-[#285BBB]/10 rounded-lg flex items-center justify-center">
                            <Folder className="h-6 w-6 text-[#285BBB]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#2C3C65]">{doc.title}</p>
                            <div className="flex items-center space-x-3 mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getFileTypeColor(doc.file_type)}`}>
                                {getFileTypeLabel(doc.file_type)}
                              </span>
                              <span className="text-xs text-[#6777A1]">{doc.filename}</span>
                              {doc.due_date && (
                                <span className="text-xs text-orange-600 flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  À rendre le {new Date(doc.due_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                            {doc.description && (
                              <p className="text-sm text-[#6777A1] mt-1">{doc.description}</p>
                            )}
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.file_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-[#285BBB] text-white rounded-lg hover:bg-[#2C3C65] transition-colors flex items-center"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* New Post Modal */}
        {showNewPostModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNewPostModal(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-[#2C3C65] mb-4">Nouvelle discussion</h2>
              <form onSubmit={createPost} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Titre</label>
                  <Input
                    value={newPostForm.title}
                    onChange={(e) => setNewPostForm({ ...newPostForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Message</label>
                  <Textarea
                    value={newPostForm.content}
                    onChange={(e) => setNewPostForm({ ...newPostForm, content: e.target.value })}
                    rows={5}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" onClick={() => setShowNewPostModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#285BBB]">
                    Publier
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EspaceElevePage;
